import { FC, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useRecoverPasswordFormManagement } from '../hooks';
import { RECOVERY_FIELD_LIMITS } from '../hooks/formManagements/useRecoverPasswordFormManagement';
import { FormTextField, BackdropLoading } from '../components';
import { MessageModal, YesNoQuestionModal } from '../modals';
import { useNavigate } from 'react-router-dom';
import { formatCountdown } from '../utils/recoveryBlock';

const keyPrefix = 'forms.RecoverPasswordForm';

export const RecoverPasswordForm: FC = (): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix });
  const [showErroMessage, setShowErroMessage] = useState<boolean>(false);
  const navigation = useNavigate();
  const {
    block,
    genericErr,
    loading,
    methods,
    pendingUsername,
    response,
    confirm,
    reject,
    submit,
  } = useRecoverPasswordFormManagement();

  const goLogin = (): void => {
    navigation('/login');
  };

  useEffect(() => {
    !!genericErr && setShowErroMessage(true);
  }, [genericErr]);

  return (
    <>
      {!!pendingUsername && (
        <YesNoQuestionModal
          message={t('modals.confirmUsername.message', {
            username: pendingUsername,
          })}
          yesLabel={t('modals.confirmUsername.yes')}
          noLabel={t('modals.confirmUsername.no')}
          onClickYes={confirm}
          onClickNo={reject}
          onClose={reject}
        />
      )}
      {!!response && (
        <MessageModal
          message={response}
          okLabel={t('modals.genericErr.ok')}
          onClickOk={goLogin}
          onClose={goLogin}
        />
      )}
      {showErroMessage && (
        <MessageModal
          message={genericErr!}
          okLabel={t('modals.genericErr.ok')}
          onClickOk={() => {
            setShowErroMessage(false);
          }}
          onClose={() => {
            setShowErroMessage(false);
          }}
        />
      )}
      {loading && <BackdropLoading />}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <Stack spacing={2} pt={3}>
            {/* Los errores de cada campo se ven en el campo (forms.md). */}
            <FormTextField
              name='identificationValue'
              label={t('identificationValue.label')}
              inputProps={{ maxLength: RECOVERY_FIELD_LIMITS.identificationMax }}
            />
            <FormTextField
              name='phone'
              label={t('phone.label')}
              inputProps={{
                maxLength: RECOVERY_FIELD_LIMITS.phoneMax,
                inputMode: 'numeric',
              }}
              onlyNumbers
            />
            {!!block && (
              <Typography variant='body1' color='error'>
                {t(`block.${block.reason}`, {
                  time: formatCountdown(block.secondsLeft),
                })}
              </Typography>
            )}
            <Stack direction='row' pt={4} spacing={2}>
              <Button
                type='button'
                color='secondary'
                variant='outlined'
                onClick={goLogin}
                fullWidth
              >
                {t('cancel.label')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={!!block || loading}
                fullWidth
              >
                {t('submit.label')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};
